import { JornadaPsicologo } from '../models/psicologo/JornadaPsicologo';

/** Mesma regra do backend (ajuste de entrada aprovado). */
export function getDataHoraCheckInEfetiva(j: JornadaPsicologo): Date {
  if (j.stAjusteAprovacaoEntrada === 1 && j.hrAjusteEntrada) {
    return new Date(j.hrAjusteEntrada);
  }
  return new Date(j.dataInicio);
}

export function getDataCheckInModal(j: JornadaPsicologo): string {
  if (j.dataCheckInJornadaAberta) {
    return j.dataCheckInJornadaAberta;
  }
  const d = getDataHoraCheckInEfetiva(j);
  return d.toLocaleDateString('pt-BR');
}

export function getHoraCheckInModal(j: JornadaPsicologo): string {
  if (j.horaCheckInJornadaAberta) {
    return j.horaCheckInJornadaAberta;
  }
  const d = getDataHoraCheckInEfetiva(j);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function getUreModal(j: JornadaPsicologo): string {
  if (j.ureJornadaFormatada) {
    return j.ureJornadaFormatada;
  }
  if (j.nomeDiretoria) {
    return `${j.codigoDiretoria} - ${j.nomeDiretoria}`;
  }
  return String(j.codigoDiretoria);
}

/**
 * CA02: com escola → código - nome; sem escola explícita na API → pode exibir `escolaJornadaTextoParaModal`
 * (ex.: "Não se aplica") ou null para omitir a linha (legado sem vínculo).
 */
export function getTextoEscolaParaModal(j: JornadaPsicologo): string | null {
  if (j.possuiEscolaVinculada === true) {
    return j.escolaJornadaFormatada ?? `${j.codigoEscola} - ${j.nomeEscola}`;
  }
  if (j.possuiEscolaVinculada === false) {
    return j.escolaJornadaTextoParaModal ?? null;
  }
  if ((j.codigoEscola ?? 0) > 0 && String(j.nomeEscola ?? '').trim()) {
    return j.escolaJornadaFormatada ?? `${j.codigoEscola} - ${j.nomeEscola}`;
  }
  return null;
}

export function possuiDataValida(value?: Date | string | null): boolean {
  if (!value) {
    return false;
  }

  const data = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(data.getTime())) {
    return false;
  }

  return data.getFullYear() > 1900;
}

export function possuiCheckoutEfetivo(jornada: JornadaPsicologo): boolean {
  if (jornada.stAjusteAprovacaoSaida === 1 && possuiDataValida(jornada.hrAjusteSaida)) {
    return true;
  }

  return possuiDataValida(jornada.dataFim);
}

/** Jornada com check-out efetivo (alinhado ao backend e GetJornadaEmAberto). */
export function jornadaEstaEncerrada(jornada: JornadaPsicologo): boolean {
  return possuiCheckoutEfetivo(jornada);
}

export function jornadaEstaAtiva(jornada?: JornadaPsicologo | null): boolean {
  return !!jornada?.codigoJornada && possuiDataValida(jornada.dataInicio) && !possuiCheckoutEfetivo(jornada);
}

export function jornadaEstaAprovada(jornada?: JornadaPsicologo | null): boolean {
  const atividades = jornada?.atividades ?? [];
  return atividades.length > 0 && atividades.every(a => (a.statusAprovacao ?? 0) > 1);
}

export function extrairCodigoJornada(jornada?: JornadaPsicologo | null): number | null {
  if (!jornada) {
    return null;
  }

  const bruto = (jornada as { codigoJornada?: number; CodigoJornada?: number }).codigoJornada
    ?? (jornada as { CodigoJornada?: number }).CodigoJornada;
  const codigo = Number(bruto);

  return Number.isFinite(codigo) && codigo > 0 ? codigo : null;
}

/**
 * #155893 — Validação de check-in (uso no clique em "+", não na visibilidade dos ícones).
 * Fonte autoritativa: GetJornadaEmAberto (codigoJornadaEmAberto), mesma do ícone verde.
 */
export function jornadaLinhaPossuiCheckinAberto(
  jornada: JornadaPsicologo | null | undefined,
  codigoJornadaEmAberto: number | null
): boolean {
  const codigoLinha = extrairCodigoJornada(jornada);
  if (codigoLinha == null) {
    return false;
  }

  if (codigoJornadaEmAberto != null && codigoJornadaEmAberto === codigoLinha) {
    return true;
  }

  return jornadaEstaAtiva(jornada);
}

/** #155893 — Inclui jornada em aberto na listagem quando o filtro de Escola a exclui (ex.: check-in na URE). */
export function mesclarJornadaAtivaNaListagem(
  listagem: JornadaPsicologo[],
  jornadaAtiva?: JornadaPsicologo | null,
  codigoJornadaEmAberto: number | null = extrairCodigoJornada(jornadaAtiva)
): JornadaPsicologo[] {
  if (!jornadaAtiva || codigoJornadaEmAberto == null) {
    return listagem;
  }

  if (listagem.some(j => extrairCodigoJornada(j) === codigoJornadaEmAberto)) {
    return listagem;
  }

  return [jornadaAtiva, ...listagem];
}

/**
 * Checkout usado no overlap de jornada retroativa — alinhado a ValidaJornada (DataFim / ajuste de saída),
 * sem somar TempoRegistro/deslocamento (horaFimMasTempoRegistro), que gerava falso conflito e horários "fantasma".
 * Jornada em aberto (sem checkout): usa "agora" como fim, para bloquear horário sobreposto à jornada ativa.
 */
export function getDataHoraCheckOutParaOverlap(j: JornadaPsicologo, agora: Date = new Date()): Date {
  if (j.stAjusteAprovacaoSaida === 1 && possuiDataValida(j.hrAjusteSaida)) {
    return new Date(j.hrAjusteSaida!);
  }

  if (possuiDataValida(j.dataFim)) {
    return new Date(j.dataFim!);
  }

  return agora;
}

/** #169031 — Overlap no mesmo dia (início ≤ fim do outro e vice-versa). Jornada aberta conta até agora. */
export function jornadasSobrepoemHorario(
  candidata: JornadaPsicologo,
  intervaloInicio: Date,
  intervaloFim: Date,
  agora: Date = new Date()
): boolean {
  const inicio =
    candidata.stAjusteAprovacaoEntrada === 1 && possuiDataValida(candidata.hrAjusteEntrada)
      ? new Date(candidata.hrAjusteEntrada!)
      : new Date(candidata.dataInicio);

  const fim = getDataHoraCheckOutParaOverlap(candidata, agora);
  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime())) {
    return false;
  }

  return intervaloInicio <= fim && inicio <= intervaloFim;
}

/** Une listagens por CD_JORNADA (ex.: grade filtrada por escola + busca ampla por CPF). */
export function mesclarJornadasPorCodigo(
  base: JornadaPsicologo[],
  extras: JornadaPsicologo[]
): JornadaPsicologo[] {
  const map = new Map<number, JornadaPsicologo>();

  for (const j of [...extras, ...base]) {
    const codigo = extrairCodigoJornada(j);
    if (codigo != null) {
      map.set(codigo, j);
    }
  }

  return Array.from(map.values());
}
